import type { TenantFormState, StepId } from "./formModel";
import { Field, FieldRow, Toggle } from "./primitives";

export interface StepProps {
  form: TenantFormState;
  update: <K extends keyof TenantFormState>(k: K, v: TenantFormState[K]) => void;
  mode: "create" | "edit";
  genPassword: () => void;
}

// ---------------------------------------------------------------------------
// 1. Identité & accès
// ---------------------------------------------------------------------------
function IdentityStep({ form, update, mode, genPassword }: StepProps) {
  return (
    <>
      <Field label="Nom de l'entreprise" required>
        <input
          type="text"
          placeholder="Ex : Acme Store"
          value={form.name}
          onChange={(e) => update("name", e.target.value)}
        />
      </Field>

      {mode === "create" ? (
        <Field
          label="Slug (identifiant URL)"
          required
          hint="Minuscules, chiffres, tirets/underscores. Ex : acme-store"
        >
          <input
            type="text"
            placeholder="acme-store"
            autoComplete="off"
            value={form.slug}
            onChange={(e) => update("slug", e.target.value)}
          />
        </Field>
      ) : (
        <Field label="Slug (non modifiable)">
          <input type="text" value={form.slug} disabled />
        </Field>
      )}

      <Field label="Email" hint="Optionnel — pour l'envoi des accès.">
        <input
          type="email"
          placeholder="contact@acme.com"
          value={form.email}
          onChange={(e) => update("email", e.target.value)}
        />
      </Field>

      <Field
        label={
          mode === "create"
            ? "Mot de passe admin"
            : "Nouveau mot de passe"
        }
        hint={
          mode === "create"
            ? "Laisser vide pour auto-générer à la création."
            : "Laisser vide pour ne pas changer le mot de passe."
        }
      >
        <div className="gen-pw">
          <input
            type="password"
            autoComplete="new-password"
            placeholder={mode === "create" ? "Laisser vide pour auto-générer" : "••••••••"}
            value={form.password}
            onChange={(e) => {
              update("password", e.target.value);
              if (!e.target.value) {
                update("generated_password", null);
                update("send_credentials_email", false);
              }
            }}
          />
          <button type="button" className="btn-sm btn-purple" onClick={genPassword}>
            Générer
          </button>
        </div>
        {form.generated_password && (
          <div className="gen-pw-preview">Mot de passe généré : {form.generated_password}</div>
        )}
      </Field>

      {((mode === "create" && form.email.trim()) ||
        (mode === "edit" && form.password && form.email.trim())) && (
        <Toggle
          checked={form.send_credentials_email}
          onChange={(v) => update("send_credentials_email", v)}
          label={
            mode === "create"
              ? "Envoyer un email de bienvenue avec les accès"
              : "Envoyer le nouveau mot de passe par email"
          }
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// 2. Intelligence
// ---------------------------------------------------------------------------
function IntelligenceStep({ form, update, mode }: StepProps) {
  return (
    <>
      <Field
        label="Clé API LLM"
        hint={
          mode === "create"
            ? "Optionnel — peut être configurée plus tard."
            : "Laisser vide pour ne pas changer la clé."
        }
      >
        <input
          type="password"
          placeholder="sk-…"
          autoComplete="off"
          value={form.openai_api_key}
          onChange={(e) => update("openai_api_key", e.target.value)}
        />
      </Field>

      <FieldRow>
        <Field label="Modèle LLM">
          <select value={form.llm_model} onChange={(e) => update("llm_model", e.target.value)}>
            <option value="gpt-4o-mini">gpt-4o-mini (recommandé)</option>
            <option value="gpt-4o">gpt-4o</option>
            <option value="gpt-4-turbo">gpt-4-turbo</option>
          </select>
        </Field>
        <Field label="Requêtes max / minute">
          <input
            type="number"
            min={1}
            max={100}
            value={form.rate_limit_per_minute}
            onChange={(e) => update("rate_limit_per_minute", Number(e.target.value) || 5)}
          />
        </Field>
      </FieldRow>

      <Field
        label="Infos contextuelles de l'entreprise"
        hint="Injectées à chaque réponse du chatbot (nom, adresse, horaires, ton…)."
      >
        <textarea
          rows={5}
          placeholder={"- Nom : Mon Entreprise\n- Adresse : ...\n- Tél : ...\n- Horaires : Lun–Sam 9h–18h"}
          value={form.business_info}
          onChange={(e) => update("business_info", e.target.value)}
        />
      </Field>
    </>
  );
}

// ---------------------------------------------------------------------------
// 3. Comportement & outils
// ---------------------------------------------------------------------------
function BehaviorStep({ form, update }: StepProps) {
  return (
    <>
      <Field
        label="Ton de l'assistant (persona)"
        hint="Contrôle uniquement le ton des réponses, pas les outils disponibles."
      >
        <select value={form.persona} onChange={(e) => update("persona", e.target.value)}>
          <option value="commerce">Commerce — proactif, oriente vers l'achat</option>
          <option value="conseil">Conseil — expert, oriente vers devis / contact</option>
          <option value="support">Support — résout, escalade si besoin</option>
        </select>
      </Field>

      <div className="sub-block">
        <Toggle
          checked={form.has_catalog}
          onChange={(v) => update("has_catalog", v)}
          label="Catalogue produits"
          hint="Active la recherche de produits et l'affichage de cartes produits."
        />
        {form.has_catalog && (
          <>
            <Toggle
              checked={form.hide_out_of_stock}
              onChange={(v) => update("hide_out_of_stock", v)}
              label="Masquer les produits hors stock"
            />
            <div className="sub-label">WooCommerce (alimente le catalogue)</div>
            <Field label="URL WooCommerce">
              <input
                type="url"
                placeholder="https://monmagasin.com"
                value={form.wc_url}
                onChange={(e) => update("wc_url", e.target.value)}
              />
            </Field>
            <FieldRow>
              <Field label="Consumer Key">
                <input
                  type="password"
                  placeholder="ck_…"
                  autoComplete="off"
                  value={form.wc_consumer_key}
                  onChange={(e) => update("wc_consumer_key", e.target.value)}
                />
              </Field>
              <Field label="Consumer Secret">
                <input
                  type="password"
                  placeholder="cs_…"
                  autoComplete="off"
                  value={form.wc_consumer_secret}
                  onChange={(e) => update("wc_consumer_secret", e.target.value)}
                />
              </Field>
            </FieldRow>
          </>
        )}
      </div>

      <div className="sub-block">
        <Toggle
          checked={form.lead_capture}
          onChange={(v) => update("lead_capture", v)}
          label="Capture de leads"
          hint="Le bot recueille un contact quand il ne peut pas répondre, et notifie par email."
        />
        {form.lead_capture && (
          <Field
            label="Email de notification des leads"
            hint="Laisser vide = email du compte tenant."
          >
            <input
              type="email"
              placeholder="leads@entreprise.com"
              value={form.lead_notification_email}
              onChange={(e) => update("lead_notification_email", e.target.value)}
            />
          </Field>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// 4. Apparence du widget
// ---------------------------------------------------------------------------
function AppearanceStep({ form, update }: StepProps) {
  return (
    <>
      <Field label="Nom du bot">
        <input
          type="text"
          placeholder="Assistant"
          value={form.bot_name}
          onChange={(e) => update("bot_name", e.target.value)}
        />
      </Field>
      <Field label="Message d'accueil">
        <input
          type="text"
          placeholder="Bonjour ! Comment puis-je vous aider ?"
          value={form.welcome_message}
          onChange={(e) => update("welcome_message", e.target.value)}
        />
      </Field>
      <Field label="Texte du champ de saisie (placeholder)">
        <input
          type="text"
          placeholder="Posez votre question…"
          value={form.placeholder_text}
          onChange={(e) => update("placeholder_text", e.target.value)}
        />
      </Field>

      <FieldRow>
        <Field label="Couleur principale">
          <input
            type="text"
            placeholder="#000000"
            value={form.primary_color}
            onChange={(e) => update("primary_color", e.target.value)}
          />
        </Field>
        <Field label="Couleur secondaire">
          <input
            type="text"
            placeholder="#FFFFFF"
            value={form.secondary_color}
            onChange={(e) => update("secondary_color", e.target.value)}
          />
        </Field>
      </FieldRow>

      <FieldRow>
        <Field label="Taille du widget">
          <select value={form.widget_size} onChange={(e) => update("widget_size", e.target.value)}>
            <option value="medium">Moyenne (défaut)</option>
            <option value="small">Petite</option>
            <option value="large">Grande</option>
          </select>
        </Field>
        <Field label="Langue">
          <select value={form.language} onChange={(e) => update("language", e.target.value)}>
            <option value="fr">Français</option>
            <option value="en">English</option>
            <option value="es">Español</option>
            <option value="de">Deutsch</option>
            <option value="ar">العربية</option>
          </select>
        </Field>
      </FieldRow>

      <Field label="Position">
        <select value={form.position} onChange={(e) => update("position", e.target.value)}>
          <option value="bottom-right">Bas droite (défaut)</option>
          <option value="bottom-left">Bas gauche</option>
        </select>
      </Field>

      <FieldRow>
        <Field label="URL logo (optionnel)">
          <input
            type="url"
            placeholder="https://…/logo.png"
            value={form.logo_url}
            onChange={(e) => update("logo_url", e.target.value)}
          />
        </Field>
        <Field label="URL icône widget (optionnel)">
          <input
            type="url"
            placeholder="https://…/icon.png"
            value={form.widget_icon_url}
            onChange={(e) => update("widget_icon_url", e.target.value)}
          />
        </Field>
      </FieldRow>

      <div className="sub-block">
        <Toggle
          checked={form.auto_open}
          onChange={(v) => update("auto_open", v)}
          label="Ouvrir automatiquement le widget (desktop, première visite)"
        />
        <Toggle
          checked={form.powered_by}
          onChange={(v) => update("powered_by", v)}
          label="Afficher « Powered by AM Solutions »"
        />
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// 5. Sécurité & permissions
// ---------------------------------------------------------------------------
function SecurityStep({ form, update }: StepProps) {
  return (
    <>
      <Field
        label="Domaines autorisés"
        hint="Un domaine par ligne. Vide = tous les domaines autorisés."
      >
        <textarea
          rows={4}
          placeholder={"https://monsite.com\nhttps://www.monsite.com"}
          value={form.allowed_origins}
          onChange={(e) => update("allowed_origins", e.target.value)}
        />
      </Field>

      <Toggle
        checked={form.can_customize_widget}
        onChange={(v) => update("can_customize_widget", v)}
        label="Autoriser le client à modifier l'apparence de son widget"
      />
    </>
  );
}

const SECTIONS: Record<StepId, (p: StepProps) => JSX.Element> = {
  identity: IdentityStep,
  intelligence: IntelligenceStep,
  behavior: BehaviorStep,
  appearance: AppearanceStep,
  security: SecurityStep,
};

export function StepSection({ id, ...props }: StepProps & { id: StepId }) {
  const Comp = SECTIONS[id];
  return <Comp {...props} />;
}
